'use client';

import { Asset } from '@/@shared/components/primitives/Asset';
import { Spacer } from '@/@shared/components/primitives/Spacer/Spacer';
import { fadeTransition, slideHorizontalTransition, slideHorizontalVariants } from '@/@shared/animations';
import { useTransAuth } from '@/@shared/hooks/useTranslations';
import GetEmail from '@/app/[locale]/(auth)/signup/(funnel)/GetEmail';
import GetPasswords from '@/app/[locale]/(auth)/signup/(funnel)/GetPasswords';
import RegisterAgreements from '@/app/[locale]/(auth)/signup/(funnel)/RegisterAgreements';
import RegisterDone from '@/app/[locale]/(auth)/signup/(funnel)/RegisterDone';
import VerifyEmail from '@/app/[locale]/(auth)/signup/(funnel)/VerifyEmail';
import { useSignupForm } from '@/app/[locale]/(auth)/signup/hooks/useSignupForm';
import styles from '@/app/[locale]/(auth)/signup/SignupForm.module.scss';
import type { SignUpType } from '@/model/zod/authZod';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';

type Props = {
	locale: string;
};

const SignupForm = ({ locale }: Props) => {
	const t = useTransAuth('signup.signupForm');

	const {
		currentStep,
		currentStepIndex,
		direction,
		methods,
		nextStep,
		sendVerificationEmail,
		verifyEmailCode,
		submitSignup,
		goToLogin,
		isSendingEmail,
		isVerifying,
		isSubmitting,
	} = useSignupForm(locale);

	const steps = ['RegisterAgreements', 'GetEmail', 'VerifyEmail', 'GetPasswords', 'RegisterDone'];

	useEffect(() => {
		window.scrollTo({ behavior: 'auto', left: 0, top: 0 });
	}, [currentStep]);

	const progressBarWidth = (stepIndex: number) => {
		const totalSteps = steps.length - 1;
		const stepPercentage = (stepIndex + 1) / totalSteps;
		return stepPercentage * 100 + '%';
	};

	const onFinalSubmit = methods.handleSubmit(async (data: SignUpType) => {
		await submitSignup(data);
	});

	return (
		<div className={styles.area}>
			<div className={styles.space}>
				<div className={styles.wrap}>
					{currentStep !== 'RegisterDone' && (
						<>
							<div className={styles.title}>
								<Asset type="icon" name="auth-icon-stepin-logo-png" size={{ height: 80 }} alt="STEPIN Tournament" />

								<Spacer size={8} />
								<div className={styles.line1}>{t('title')}</div>
								<Spacer size={8} />
							</div>

							<div className={styles.progress}>
								<motion.div
									animate={{ width: progressBarWidth(currentStepIndex) }}
									className={styles.progressBar}
									transition={fadeTransition}
								/>
								<div className={styles.progressBarBg} />
							</div>
						</>
					)}

					<div className={styles.frame}>
						<FormProvider {...methods}>
							<form className={styles.container} onSubmit={onFinalSubmit}>
								<AnimatePresence custom={direction} initial={false} mode={'wait'}>
									<motion.div
										animate="animate"
										className={styles.motion}
										custom={direction}
										exit="exit"
										initial="initial"
										key={currentStep}
										transition={slideHorizontalTransition}
										variants={slideHorizontalVariants}
									>
										{currentStep === 'RegisterAgreements' && <RegisterAgreements locale={locale} onNext={nextStep} />}
										{currentStep === 'GetEmail' && (
											<GetEmail
												locale={locale}
												onNext={nextStep}
												onSendEmail={sendVerificationEmail}
												isSending={isSendingEmail}
											/>
										)}
										{currentStep === 'VerifyEmail' && (
											<VerifyEmail
												locale={locale}
												onNext={nextStep}
												onVerify={verifyEmailCode}
												onResend={sendVerificationEmail}
												isVerifying={isVerifying}
											/>
										)}
										{currentStep === 'GetPasswords' && <GetPasswords locale={locale} onNext={nextStep} />}
										{currentStep === 'RegisterDone' && <RegisterDone locale={locale} onNext={goToLogin} />}
									</motion.div>
								</AnimatePresence>
							</form>
						</FormProvider>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SignupForm;
